/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { SessionService } from './session.service';
import { DecodedToken } from './token/models';

declare let gtag: Function;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private jwtPayload: DecodedToken;

  constructor(private readonly sessionService: SessionService) {
    this.jwtPayload = this.sessionService.getJWTPayload();
  }

  /**
   * This method only registers pageviews. All Components should use this on init.
   * @param componentName  - use the 'selector' name for consistency
   * @param pageTitle - a name that best defines the component. Eg: credit page - 'credit'
   */
  public sendPageViewAnalytics(componentName: string, pageTitle: string): void {
    this.setUserIdForEvent();
    if (this.jwtPayload) {
      gtag('event', 'page_view', {
        page_title: pageTitle,
        screen_name: componentName,
      });
      gtag('set', 'user_properties', {
        UserId: this.jwtPayload['userId'],
      });
    }
  }

  /**
   * Any event based analytics
   * @param actionName - identify the user action with an appropriate identifier eg: 'login'
   * @param value - google analytics accepts integers, we could use something like 0/1 for success/failure
   */
  public sendEventAnalytics(screenName: string, actionName: string): void {
    const eventName = screenName.concat('_').concat(actionName);
    const analyticsPayload: any = {
      screen_name: screenName,
      event_label: eventName,
    };
    this.setUserIdForEvent();
    if (this.jwtPayload) {
      gtag('event', eventName, analyticsPayload);
      gtag('set', 'user_properties', {
        UserId: this.jwtPayload['userId'],
      });
    }
  }

  /**
   * Any event based analytics with custom payload
   * @param actionName - identify the user action with an appropriate identifier eg: 'login'
   * @param customPayload - any json object
   */
  public sendCustomAnalytics(actionName: string, customPayload: any): void {
    customPayload['event_category'] = 'user-action';
    customPayload['member_id'] = this.jwtPayload['custom:memberId'];
    const customMap = {
      custom_map: customPayload,
    };
    this.setUserIdForEvent();
    if (this.jwtPayload) {
      gtag('event', actionName, customMap);
      gtag('set', 'user_properties', {
        UserId: this.jwtPayload['userId'],
      });
    }
  }

  private setUserIdForEvent(): void {
    if (!this.jwtPayload) {
      this.jwtPayload = this.sessionService.getJWTPayload();
    }
  }
}
